import type { Localized } from "../i18n";
import type { LevelNumber } from "./types";

/**
 * The Turo World pose bank — TW-4.
 *
 * 🔴 GEOMETRY ONLY. NO TOLERANCE, NO HOLD, NO TIMEOUT, AND THAT IS ADR-031.
 * The L1 draft put `enterTol`, `exitTol`, `holdMs` and `timeoutMs` on every pose
 * here. Two things reject that: `check-game-contract.mjs` fails the build on
 * *"a game may not define a threshold"*, and `crossGame.test.ts` asserts a
 * level's motor data carries only `{kind, countsTo, zones, expect}` with a
 * comment naming `tolerance` as the field the rule exists to prevent.
 *
 * It is also the better split. Per-pose tolerance IS per-pose motor difficulty,
 * and a level-5 T-pose must be the same T-pose as a level-1 one. This table says
 * WHICH JOINTS AND WHAT ANGLE. How close counts as close enough lives in
 * `vision/pose/poseMatch.ts`, is the same for every pose, and is re-tuned
 * against children rather than against a test (ADR-024, ADR-042).
 *
 * ─── EIGHTEEN POSES — THE DRAFT'S BANK, COMPLETE ────────────────────────────
 *
 * §6.3's seven upper, §6.4's four full, §6.5's seven free. Nineteen drawings
 * back them: the eighteen plus `idle`, which is the resting frame Turo returns
 * to between beats and is deliberately not something a child is asked to copy.
 *
 * ⚠️ IT WAS FOURTEEN UNTIL THE MISSING FOUR WERE DRAWN, AND THE GAP WAS REAL:
 * with `star_arms`, `prayer_up`, `wave` and a second `march` frame absent, a
 * desk-distance child had exactly three poses for beats 5–7 and no variety
 * between repeat runs. `PENDING_SPRITES` is empty now and a test asserts it —
 * if it ever grows again, that is the shortfall coming back.
 *
 * ⚠️ AND `prayer_up` ARRIVING IS WHAT LET `tree_pose` GO BACK TO BEING FREE.
 * It had been graded on its arms alone as a stand-in, with the standing leg
 * unmeasured. §6.5 lists it as free for a reason; the substitution is undone.
 */

/* ────────────────────────────────────────── locked duplicates of the kit ── */

/**
 * 🔴 A DELIBERATE, LOCKED DUPLICATE OF `Measure` IN `vision/pose/angles.ts`,
 * exactly as `HelpActName` duplicates the kit's `HelpAct`.
 *
 * The duplication is a layering consequence rather than an oversight: content
 * may not import from `features/games/`, and a curriculum table that reached
 * into the vision kit would make the kit un-refactorable without a content
 * migration. `turo-world-poses.test.ts` compares the two lists, which is what
 * makes the copy safe.
 */
export type PoseAngleMeasure =
  "elbow" | "shoulderAbduction" | "knee" | "hipFlexion" | "torsoLean";

/**
 * WHERE the hands are, in torso units — the second measurement family, added
 * for L2/L3.
 *
 * 🔴 L2 AND L3 CANNOT BE GRADED ON ANGLES ALONE, WHICH IS WHY THIS EXISTS.
 * `TURO_WORLD_L2_L3_PLAN.md` §5 specifies 16 of its 24 poses in terms angles
 * cannot express — "wrists cross midline", "within 0.4T of the OPPOSITE
 * shoulder", "LOWER half of the torso". `arms_folded` and `self_hug` have
 * near-identical joint angles; only where the wrists end up separates them.
 *
 * ⚠️ THE UNITS DIFFER FROM THE ANGLE FAMILY, AND THE KIT GRADES THEM APART.
 * `poseMatch.ts` carries one tolerance per family; a displacement routed to the
 * degrees tolerance would be satisfied by any pose at all.
 */
export type PoseTorsoMeasure =
  | "wristSeparation"
  | "wristHeightInTorso"
  | "wristToOppositeShoulder"
  | "wristToNose"
  | "wristAboveElbow"
  | "wristMidlineOffset";

export type PoseMeasure = PoseAngleMeasure | PoseTorsoMeasure;

/** Locked duplicate of the kit's `Side`. Same argument as `PoseMeasure`. */
export type PoseSide = "left" | "right";

/** Locked duplicate of the keys of `LM` in `vision/calibration/geometry.ts`. */
export type LandmarkName =
  | "nose"
  | "leftShoulder"
  | "rightShoulder"
  | "leftElbow"
  | "rightElbow"
  | "leftWrist"
  | "rightWrist"
  | "leftHip"
  | "rightHip"
  | "leftKnee"
  | "rightKnee"
  | "leftAnkle"
  | "rightAnkle";

/** Landmarks a `frame: upper` pose is allowed to require — §6.2. */
export const UPPER_LANDMARKS: ReadonlySet<LandmarkName> = new Set<LandmarkName>(
  [
    "nose",
    "leftShoulder",
    "rightShoulder",
    "leftElbow",
    "rightElbow",
    "leftWrist",
    "rightWrist",
    "leftHip",
    "rightHip",
  ],
);

/* ────────────────────────────────────────────────────────────── the shape ── */

/**
 * Which framing the pose can be graded in.
 *
 * `upper` works at laptop-at-desk distance; `full` needs hips, knees and ankles
 * in shot. The selector serves only what Beat 0 found, and **nobody is ever told
 * to move the camera** — §6.2.
 */
export type PoseFrame = "upper" | "full";

/** A pose held still, or a movement repeated. */
export type PoseKind = "hold" | "repetition";

/**
 * How good this pose is as a body-scale measurement — §2.1.
 *
 * `high` means both arms fully extended, wrists far from the torso, minimal
 * self-occlusion. Only those may be pinned to the calibration beats.
 */
export type CalibrationQuality = "high" | "medium" | "low";

/** One requirement — an angle or a displacement. Nothing about how close. */
export interface PoseTargetContent {
  measure: PoseMeasure;
  side: PoseSide;
  /** Degrees for an angle measure; TORSO UNITS for a displacement one. */
  value: number;
  /**
   * How `value` is compared. Omitted means `eq` — every L1 pose predates
   * displacements and is unchanged.
   *
   * ⚠️ FOR OPEN-ENDED FACTS, NOT FOR EASIER GRADING. "Wrist above elbow" is true
   * at 0.1T and equally true at 0.4T; written as `eq` it would demand one exact
   * height and fail a child who lifted their hand further.
   */
  op?: "eq" | "gte" | "lte";
}

export interface TuroPose {
  id: string;
  frame: PoseFrame;
  /** False for a pose that is shown and copied but always succeeds — §6.5. */
  graded: boolean;
  type: PoseKind;
  availableFrom: LevelNumber;
  calibrationQuality: CalibrationQuality;
  /** True when the pose is one-sided and either side counts — §6.7. */
  mirrorTolerant: boolean;
  requiredLandmarks: readonly LandmarkName[];
  targets: readonly PoseTargetContent[];
  /** How many repeats, for `type: 'repetition'`. A count is content. */
  reps?: number;
  /** → public/shared/audio/{lang}/{audioKey}.mp3. Pre-recorded, never TTS (ADR-011). */
  audio: { cue: string; hint?: string };
  /** What Turo says. Carried for the studio, the screen reader and the adult. */
  cue: Localized<string>;
  /** Played once, part-way through an attempt. Graded poses only. */
  hint?: Localized<string>;
  /** Frame id in `tw-character-atlas`. The ghost shares the same frame map. */
  sprite: string;
  ghost: string;
}

/* ─────────────────────────────────────────────────────── graded · upper ── */

const T_POSE: TuroPose = {
  id: "t_pose",
  frame: "upper",
  graded: true,
  type: "hold",
  availableFrom: 1,
  /*
   * 🔴 THE BEST CALIBRATION POSE IN THE BANK, AND BEAT 3 IS PINNED TO IT (§2.1).
   * Both arms straight and horizontal puts the wrists further from the torso
   * than any other pose while keeping every landmark unoccluded — which is the
   * frame a shoulder-width and arm-span measurement wants.
   */
  calibrationQuality: "high",
  mirrorTolerant: false,
  requiredLandmarks: [
    "leftShoulder",
    "rightShoulder",
    "leftElbow",
    "rightElbow",
    "leftWrist",
    "rightWrist",
    "leftHip",
    "rightHip",
  ],
  targets: [
    { measure: "elbow", side: "left", value: 150, op: "gte" },
    { measure: "elbow", side: "right", value: 150, op: "gte" },
    { measure: "shoulderAbduction", side: "left", value: 90 },
    { measure: "shoulderAbduction", side: "right", value: 90 },
  ],
  audio: { cue: "tw_pose_t_pose_cue", hint: "tw_pose_t_pose_hint" },
  cue: { en: "Arms out wide, like a big tree!" },
  hint: { en: "Make your arms nice and straight!" },
  sprite: "t_pose",
  ghost: "t_pose",
};

const ARMS_UP: TuroPose = {
  id: "arms_up",
  frame: "upper",
  graded: true,
  type: "hold",
  availableFrom: 1,
  /** The second pinned calibration pose — Beat 4. Full vertical extension. */
  calibrationQuality: "high",
  mirrorTolerant: false,
  requiredLandmarks: [
    "nose",
    "leftShoulder",
    "rightShoulder",
    "leftElbow",
    "rightElbow",
    "leftWrist",
    "rightWrist",
    "leftHip",
    "rightHip",
  ],
  targets: [
    { measure: "elbow", side: "left", value: 150, op: "gte" },
    { measure: "elbow", side: "right", value: 150, op: "gte" },
    { measure: "shoulderAbduction", side: "left", value: 170 },
    { measure: "shoulderAbduction", side: "right", value: 170 },
  ],
  audio: { cue: "tw_pose_arms_up_cue", hint: "tw_pose_arms_up_hint" },
  cue: { en: "Reach up high to the sky!" },
  hint: { en: "Stretch them higher!" },
  sprite: "arms_up",
  ghost: "arms_up",
};

const HANDS_ON_HIPS: TuroPose = {
  id: "hands_on_hips",
  frame: "upper",
  graded: true,
  type: "hold",
  availableFrom: 1,
  /** Wrists close to the torso — a poor scale measurement, a fine pose. */
  calibrationQuality: "low",
  mirrorTolerant: false,
  requiredLandmarks: [
    "leftShoulder",
    "rightShoulder",
    "leftElbow",
    "rightElbow",
    "leftWrist",
    "rightWrist",
    "leftHip",
    "rightHip",
  ],
  /*
   * ⚠️ THE DRAFT SPECIFIES "wrist within 0.15 of hip", WHICH IS A DISTANCE, AND
   * THIS BANK HAS NO DISTANCE MEASURE. ADR-042 made every measurement angular so
   * it would be scale-free; adding one normalised distance would reintroduce the
   * torso denominator for a single pose. A bent elbow with the upper arm close to
   * the body is the same shape expressed in angles, and it is what the drawing
   * shows.
   */
  targets: [
    { measure: "elbow", side: "left", value: 90 },
    { measure: "elbow", side: "right", value: 90 },
    { measure: "shoulderAbduction", side: "left", value: 30 },
    { measure: "shoulderAbduction", side: "right", value: 30 },
  ],
  audio: {
    cue: "tw_pose_hands_on_hips_cue",
    hint: "tw_pose_hands_on_hips_hint",
  },
  cue: { en: "Hands on your hips!" },
  hint: { en: "Put both hands on your hips!" },
  sprite: "hands_on_hips",
  ghost: "hands_on_hips",
};

const SIDE_BEND: TuroPose = {
  id: "side_bend",
  frame: "upper",
  graded: true,
  type: "hold",
  availableFrom: 1,
  calibrationQuality: "low",
  /** One-sided: the child may lean whichever way they read from Turo (§6.7). */
  mirrorTolerant: true,
  requiredLandmarks: [
    "leftShoulder",
    "rightShoulder",
    "leftElbow",
    "rightElbow",
    "leftWrist",
    "rightWrist",
    "leftHip",
    "rightHip",
  ],
  targets: [
    /*
     * 🔴 `gte 45`, AND IT WAS `25` — WHICH ACCEPTED STANDING STRAIGHT.
     * `torsoLean` is unsigned and reads 0° upright, and `poseMatch` grades an
     * angle within ±25° to enter and ±40° to hold, so 25° admitted 0° and a
     * child with one arm up and no lean at all was "bending like a tree". A
     * deeper bend is the same pose, so it is open-ended rather than an exact
     * angle: in practice the lean must reach 20° to start and stay above 5°
     * to hold. `turos-world/sideBend.test.ts` pins both against the kit's tolerances.
     */
    { measure: "torsoLean", side: "left", value: 45, op: "gte" },
    { measure: "shoulderAbduction", side: "left", value: 170 },
  ],
  audio: { cue: "tw_pose_side_bend_cue", hint: "tw_pose_side_bend_hint" },
  cue: { en: "Lean over like a bendy tree!" },
  hint: { en: "Lean a little more!" },
  sprite: "side_bend",
  ghost: "side_bend",
};

const STAR_ARMS: TuroPose = {
  id: "star_arms",
  frame: "upper",
  graded: true,
  type: "hold",
  availableFrom: 1,
  /*
   * ⚠️ A GENUINELY GOOD CALIBRATION POSE, AND IT IS NOT PINNED. Both arms are
   * fully extended with the wrists far from the torso and nothing occluded —
   * §2.1's criteria exactly. It is `high` because that is true, and beats 3–4
   * stay `t_pose` and `arms_up` because §2.1 names those two. Which poses are
   * pinned is a decision recorded in `PINNED_CALIBRATION_POSES`, not something
   * derived from this field — otherwise adding a good pose would silently
   * reorder the session.
   */
  calibrationQuality: "high",
  mirrorTolerant: false,
  requiredLandmarks: [
    "leftShoulder",
    "rightShoulder",
    "leftElbow",
    "rightElbow",
    "leftWrist",
    "rightWrist",
    "leftHip",
    "rightHip",
  ],
  targets: [
    { measure: "elbow", side: "left", value: 150, op: "gte" },
    { measure: "elbow", side: "right", value: 150, op: "gte" },
    { measure: "shoulderAbduction", side: "left", value: 135 },
    { measure: "shoulderAbduction", side: "right", value: 135 },
  ],
  audio: { cue: "tw_pose_star_arms_cue", hint: "tw_pose_star_arms_hint" },
  cue: { en: "Make a big star with your arms!" },
  hint: { en: "Open them wider!" },
  sprite: "star_arms",
  ghost: "star_arms",
};

const PRAYER_UP: TuroPose = {
  id: "prayer_up",
  frame: "upper",
  graded: true,
  type: "hold",
  availableFrom: 1,
  calibrationQuality: "medium",
  mirrorTolerant: false,
  /*
   * 🔴 THIS DRAWING ARRIVING IS WHAT LET `tree_pose` GO BACK TO BEING FREE.
   * Before it existed, `tree_pose`'s prayer-hands were graded as a stand-in and
   * its standing leg simply not measured — a substitution that worked but read
   * as a pose the game was only half looking at. §6.5 lists `tree_pose` as free
   * for a reason (the balance is foreshortened and self-occluding); this pose is
   * the same arms with both feet on the floor, which is the half worth grading.
   */
  requiredLandmarks: [
    "nose",
    "leftShoulder",
    "rightShoulder",
    "leftElbow",
    "rightElbow",
    "leftWrist",
    "rightWrist",
    "leftHip",
    "rightHip",
  ],
  targets: [
    { measure: "elbow", side: "left", value: 150 },
    { measure: "elbow", side: "right", value: 150 },
    { measure: "shoulderAbduction", side: "left", value: 170 },
    { measure: "shoulderAbduction", side: "right", value: 170 },
  ],
  audio: { cue: "tw_pose_prayer_up_cue", hint: "tw_pose_prayer_up_hint" },
  cue: { en: "Hands together, way up high!" },
  hint: { en: "Press your hands together up high!" },
  sprite: "prayer_up",
  ghost: "prayer_up",
};

const WAVE: TuroPose = {
  id: "wave",
  frame: "upper",
  graded: true,
  type: "hold",
  availableFrom: 1,
  calibrationQuality: "low",
  /** One-sided — a child waves with whichever hand they wave with (§6.7). */
  mirrorTolerant: true,
  requiredLandmarks: [
    "leftShoulder",
    "rightShoulder",
    "leftElbow",
    "rightElbow",
    "leftWrist",
    "rightWrist",
    "leftHip",
    "rightHip",
  ],
  /*
   * ⚠️ §6.3 SAYS "wrist above shoulder", WHICH IS A POSITION AND NOT AN ANGLE.
   * An upper arm lifted past horizontal with the elbow bent puts the wrist there
   * by construction, and it is what the drawing shows — so the pair of angles
   * says the same thing without reintroducing a coordinate comparison ADR-042
   * removed the denominator for.
   */
  targets: [
    { measure: "elbow", side: "left", value: 105 },
    { measure: "shoulderAbduction", side: "left", value: 120 },
  ],
  audio: { cue: "tw_pose_wave_cue", hint: "tw_pose_wave_hint" },
  cue: { en: "Wave hello to me!" },
  hint: { en: "Lift your hand up and wave!" },
  sprite: "wave",
  ghost: "wave",
};

/**
 * `tree_pose` — shown, copied, never graded. §6.5, as written.
 *
 * The balance is the problem: one foot on the opposite knee is foreshortened
 * toward the camera and the standing leg self-occludes. `prayer_up` grades the
 * same arms with both feet down.
 */
const TREE_POSE: TuroPose = {
  id: "tree_pose",
  frame: "full",
  graded: false,
  type: "hold",
  availableFrom: 1,
  calibrationQuality: "low",
  mirrorTolerant: false,
  requiredLandmarks: [],
  targets: [],
  audio: { cue: "tw_pose_tree_pose_cue" },
  cue: { en: "Stand tall like a tree!" },
  sprite: "tree_pose",
  ghost: "tree_pose",
};

/* ──────────────────────────────────────────────────────── graded · full ── */

const STAR_JUMP: TuroPose = {
  id: "star_jump",
  frame: "full",
  graded: true,
  type: "repetition",
  availableFrom: 1,
  calibrationQuality: "medium",
  mirrorTolerant: false,
  /** A count is content — `countsTo` set that precedent (ADR-031). */
  reps: 4,
  requiredLandmarks: [
    "leftShoulder",
    "rightShoulder",
    "leftElbow",
    "rightElbow",
    "leftWrist",
    "rightWrist",
    "leftHip",
    "rightHip",
    "leftKnee",
    "rightKnee",
  ],
  targets: [
    { measure: "shoulderAbduction", side: "left", value: 170 },
    { measure: "shoulderAbduction", side: "right", value: 170 },
  ],
  audio: { cue: "tw_pose_star_jump_cue", hint: "tw_pose_star_jump_hint" },
  cue: { en: "Jump like a star!" },
  hint: { en: "Jump and open wide!" },
  sprite: "star_jump",
  ghost: "star_jump",
};

const KNEE_RAISE: TuroPose = {
  id: "knee_raise",
  frame: "full",
  graded: true,
  type: "hold",
  availableFrom: 1,
  calibrationQuality: "low",
  /** One-sided — either knee counts (§6.7). */
  mirrorTolerant: true,
  requiredLandmarks: [
    "leftShoulder",
    "rightShoulder",
    "leftHip",
    "rightHip",
    "leftKnee",
    "rightKnee",
    "leftAnkle",
    "rightAnkle",
  ],
  targets: [
    { measure: "knee", side: "left", value: 90 },
    { measure: "hipFlexion", side: "left", value: 100 },
  ],
  audio: { cue: "tw_pose_knee_raise_cue", hint: "tw_pose_knee_raise_hint" },
  cue: { en: "Lift your knee up high!" },
  hint: { en: "Lift that knee higher!" },
  sprite: "knee_raise",
  ghost: "knee_raise",
};

const SQUAT: TuroPose = {
  id: "squat",
  frame: "full",
  graded: true,
  type: "hold",
  availableFrom: 1,
  /*
   * ⚠️ THE DRAFT'S OWN TIER-B POSE — *"foreshortening risk, widen tolerance"*.
   * The tolerance cannot be widened here (ADR-031), and it should not be: a
   * squat read head-on foreshortens the thigh, so the knee angle is
   * systematically wrong rather than noisy, and a wider band would accept a
   * standing child rather than fix the measurement. Recorded as `low` so the
   * selector can prefer better poses and the playtest knows where to look first.
   */
  calibrationQuality: "low",
  mirrorTolerant: false,
  requiredLandmarks: [
    "leftShoulder",
    "rightShoulder",
    "leftHip",
    "rightHip",
    "leftKnee",
    "rightKnee",
    "leftAnkle",
    "rightAnkle",
  ],
  targets: [
    { measure: "knee", side: "left", value: 115 },
    { measure: "knee", side: "right", value: 115 },
    { measure: "hipFlexion", side: "left", value: 95 },
    { measure: "hipFlexion", side: "right", value: 95 },
  ],
  audio: { cue: "tw_pose_squat_cue", hint: "tw_pose_squat_hint" },
  cue: { en: "Sit down low like a frog!" },
  hint: { en: "Bend your knees a bit more!" },
  sprite: "squat",
  ghost: "squat",
};

const MARCH: TuroPose = {
  id: "march",
  frame: "full",
  graded: true,
  type: "repetition",
  availableFrom: 1,
  calibrationQuality: "low",
  /** Alternating by definition, so either knee starts it (§6.7). */
  mirrorTolerant: true,
  /** §6.4: "knee above hip line, alternating ×6". A count is content. */
  reps: 6,
  requiredLandmarks: [
    "leftShoulder",
    "rightShoulder",
    "leftHip",
    "rightHip",
    "leftKnee",
    "rightKnee",
    "leftAnkle",
    "rightAnkle",
  ],
  targets: [
    { measure: "knee", side: "left", value: 90 },
    { measure: "hipFlexion", side: "left", value: 100 },
  ],
  audio: { cue: "tw_pose_march_cue", hint: "tw_pose_march_hint" },
  cue: { en: "March with me, up and down!" },
  hint: { en: "Keep those knees going!" },
  sprite: "march",
  ghost: "march",
};

/* ────────────────────────────────────────────────── free · never graded ── */

/**
 * Shown, copied, **always succeeds** — §6.5.
 *
 * 🔴 NOT SECOND-CLASS CONTENT. These carry the cool-down and most of the variety
 * on a repeat run, and a child cannot tell them from a graded pose: the demo,
 * the ghost and the praise are identical. What differs is that nothing is
 * measured, because each is self-occluding, foreshortened toward the camera, or
 * a motion cycle rather than a held shape — and a measurement that cannot be
 * trusted must not be allowed to decide whether a four-year-old succeeded.
 */
const free = (id: string, cue: Localized<string>): TuroPose => ({
  id,
  frame: "full",
  graded: false,
  type: "hold",
  availableFrom: 1,
  calibrationQuality: "low",
  mirrorTolerant: false,
  requiredLandmarks: [],
  targets: [],
  audio: { cue: `tw_pose_${id}_cue` },
  cue,
  sprite: id,
  ghost: id,
});

const FORWARD_BEND = free("forward_bend", {
  en: "Reach down to your toes!",
});
const FROG_CROUCH = free("frog_crouch", {
  en: "Crouch down like a little frog!",
});
const BEAR_CRAWL = free("bear_crawl", {
  en: "Walk like a big bear!",
});
const SIT_LEGS_OUT = free("sit_legs_out", {
  en: "Sit down, legs out long!",
});
const RUN_CYCLE = free("run_cycle", {
  en: "Run on the spot with me!",
});

/** 🔴 The designated cool-down — §6.5. Beat 8 is pinned to this. */
const SIT_CROSS_LEGGED = free("sit_cross_legged", {
  en: "Sit criss-cross and take a big breath.",
});

/* ────────────────────────────────────────────────────────────── the bank ── */

export const TURO_POSES: readonly TuroPose[] = [
  T_POSE,
  ARMS_UP,
  STAR_ARMS,
  HANDS_ON_HIPS,
  SIDE_BEND,
  PRAYER_UP,
  WAVE,
  STAR_JUMP,
  MARCH,
  KNEE_RAISE,
  SQUAT,
  TREE_POSE,
  FORWARD_BEND,
  FROG_CROUCH,
  BEAR_CRAWL,
  SIT_LEGS_OUT,
  RUN_CYCLE,
  SIT_CROSS_LEGGED,
];

/**
 * 🔴 THE TWO CALIBRATION POSES, PINNED, IN ORDER — §2.1.
 *
 * *"Pose order is a calibration decision, not an aesthetic one. Do not let a
 * later content pass shuffle them out of position."* Draft §10 validation 11
 * exists to enforce exactly this, and it is asserted against this constant
 * rather than against whatever a session template happens to contain.
 */
export const PINNED_CALIBRATION_POSES: readonly string[] = [
  "t_pose",
  "arms_up",
];

/** The pose Beat 8 cools down on. */
export const COOLDOWN_POSE = "sit_cross_legged";

/**
 * Drawings the draft grades that the supplied sheet does not contain.
 *
 * ⚠️ RECORDED AS DATA RATHER THAN AS A COMMENT, so the shortfall is countable
 * and a test can assert it has not silently grown. Each needs one drawing before
 * the pose can join the bank; the geometry is already written in the draft §6.3.
 */
export const PENDING_SPRITES: readonly string[] = [];

export const poseById = (id: string): TuroPose | undefined =>
  TURO_POSES.find((p) => p.id === id);

/** The poses a given framing can serve — §6.2's selector. */
export const posesForFrame = (frame: PoseFrame): TuroPose[] =>
  TURO_POSES.filter((p) => (frame === "full" ? true : p.frame === "upper"));
